import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Product } from '../../types/product';
import { Image, USO_IMAGEN_LABELS, UsoImagenType } from '../../types/image';
import Swal from 'sweetalert2';

interface FileWithUsage {
    file: File;
    usoImagen: UsoImagenType;
}  
interface ImageUploaderProps {
    product: Product;
    onUpload: (files: FileWithUsage[]) => void;
    uploading: boolean;
    existingImages: Image[];
}

 



const ImageUploader: React.FC<ImageUploaderProps> = ({
    product,
    onUpload,
    uploading,
    existingImages
}) => {
    //const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<FileWithUsage[]>([]);
    const [duplicateFiles, setDuplicateFiles] = useState<string[]>([]);
    const [invalidCodeFiles, setInvalidCodeFiles] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Limpiar selección cuando cambia el producto
    useEffect(() => {
        clearAllSelection();
    }, [product.productCode]);

    // Función para limpiar toda la selección y mensajes
    const clearAllSelection = () => {
        setSelectedFiles([]);
        setDuplicateFiles([]);
        setInvalidCodeFiles([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Función para verificar duplicados
    const checkForDuplicates = (files: File[]): { uniqueFiles: File[], duplicates: string[] } => {
        const existingFileNames = new Set(
            existingImages.map(img => img.name.toLowerCase())
        );

        const uniqueFiles: File[] = [];
        const duplicates: string[] = [];

        files.forEach(file => {
            if (existingFileNames.has(file.name.toLowerCase())) {
                duplicates.push(file.name);
            } else {
                uniqueFiles.push(file);
            }
        });

        return { uniqueFiles, duplicates };
    };

    const handleCambioUsoImagen = (index: number, usoImagen: UsoImagenType) => {
        setSelectedFiles(prevFiles => prevFiles.map((file, i) => 
            i === index ? { ...file, usoImagen } : file
        ));
    };

    // Función para verificar código de producto en el nombre
    const checkProductCodeInFileName = (files: File[]): { validFiles: File[], invalidFiles: string[] } => {
        const validFiles: File[] = [];
        const invalidFiles: string[] = [];

        files.forEach(file => {
            // Verificar si el nombre contiene el código del producto
            const fileName = file.name.toLowerCase();
            const productCode = product.productCode.toLowerCase();

            if (fileName.includes(productCode)) {
                validFiles.push(file);
            } else {
                invalidFiles.push(file.name);
            }
        });

        return { validFiles, invalidFiles };
    };

    // Manejar archivos seleccionados vía input tradicional
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const files = Array.from(e.target.files);
            processSelectedFiles(files);
        }
    };

    // Procesar archivos seleccionados (desde dropzone o input)
    const processSelectedFiles = (files: File[]) => {
        // 1. Primero verificar código de producto
        const { validFiles: codeValidFiles, invalidFiles: codeInvalidFiles } = checkProductCodeInFileName(files);


        if (codeInvalidFiles.length > 0) {
            setInvalidCodeFiles(prev => [...prev, ...codeInvalidFiles]);
            showInvalidCodeWarning(codeInvalidFiles);
        }

        // 2. Luego verificar duplicados en los archivos válidos por código
        const { uniqueFiles, duplicates } = checkForDuplicates(codeValidFiles);

        // 3. Convertir File[] a FileWithUsage[] con valor por defecto "GENERAL"
        const filesWithUsage: FileWithUsage[] = uniqueFiles.map(file => ({
            file,
            usoImagen: 'GENERAL' as UsoImagenType
        }));

        setSelectedFiles(prev => [...prev, ...filesWithUsage]);

        if (duplicates.length > 0) {
            setDuplicateFiles(prev => [...prev, ...duplicates]);
            showDuplicateWarning(duplicates);
        }
    };

    // Mostrar advertencia de archivos con código inválido
    const showInvalidCodeWarning = (invalidFiles: string[]) => {
        Swal.fire({
            icon: 'error',
            title: 'Código de producto incorrecto',
            html: `Los siguientes archivos no contienen el código <strong>${product.productCode}</strong>:<br>
                   ${invalidFiles.slice(0, 3).map(name => `• ${name}`).join('<br>')}
                   ${invalidFiles.length > 3 ? `<br>...y ${invalidFiles.length - 3} más` : ''}<br><br>
                   <small>Los nombres deben incluir el código del producto.</small>`,
            toast: true,
            position: 'top-end',
            timer: 6000
        });
    };

    // Mostrar advertencia de archivos duplicados
    const showDuplicateWarning = (duplicates: string[]) => {
        Swal.fire({
            icon: 'warning',
            title: 'Archivos duplicados',
            html: `Se omitieron <strong>${duplicates.length}</strong> archivos que ya existen:<br>
                   ${duplicates.slice(0, 3).map(name => `• ${name}`).join('<br>')}
                   ${duplicates.length > 3 ? `<br>...y ${duplicates.length - 3} más` : ''}`,
            toast: true,
            position: 'top-end',
            timer: 5000
        });
    };

    // Configuración de Dropzone
    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) return;
        processSelectedFiles(acceptedFiles);
    }, [product.productCode, existingImages]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/jpeg': ['.jpeg', '.jpg'],
            'image/png': ['.png'],
            'image/gif': ['.gif'],
            'image/webp': ['.webp']
        },
        multiple: true,
        maxFiles: 10,
        maxSize: 10 * 1024 * 1024, // 10 MB
        disabled: uploading
    });

    const localHandleUpload = async () => {
        if (selectedFiles.length > 0) {
            try {
                await onUpload(selectedFiles);
                
                // ✅ LIMPIAR TODO DESPUÉS DE SUBIR EXITOSAMENTE
                clearAllSelection();

                // Mostrar mensaje de éxito
                Swal.fire({
                    icon: 'success',
                    title: '¡Imágenes subidas!',
                    text: `${selectedFiles.length} archivo(s) subido(s) correctamente.`,
                    toast: true,
                    position: 'top-end',
                    timer: 3000
                });

            } catch (error :any) {
                // No limpiar si hay error, para que el usuario pueda reintentar
                console.error('Error al subir imágenes:', error);
                
                Swal.fire({
                    icon: 'error',
                    title: 'Error al subir imágen',
                    text: 'No se pudieron subir las imágenes: ' + error.message ,
                    toast: true,
                    position: 'top-end',
                    timer: 10000
                });
            }
        }
    };

    const handleClearSelection = () => {
        clearAllSelection();
        Swal.fire({
            icon: 'info',
            title: 'Selección limpiada',
            toast: true,
            position: 'top-end',
            timer: 2000
        });
    };

    const removeSingleFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Calcular estadísticas para mostrar al usuario
    const totalSelected = selectedFiles.length;
    const totalOmitted = duplicateFiles.length + invalidCodeFiles.length;

    return (
        <div className="card mb-4">
            {/* <div className="card-header bg-light">
                <h5 className="card-title mb-0">Subir nuevas imágenes</h5>
                <small className="text-muted">
                    Código: <strong>{product.productCode}</strong>
                </small>
            </div> */}
            <div className="card-body">
                {/* Instrucciones */}
                <div className="alert alert-info mb-3">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Importante:</strong> Los nombres de archivo deben contener el código
                    <strong className="mx-1">"{product.productCode}"</strong>
                    para ser aceptados.
                </div>

                {/* Área de Dropzone */}
                <div
                    {...getRootProps()}
                    className={`dropzone-area ${isDragActive ? 'active' : ''} mb-3 p-4 border rounded text-center`}
                    style={{
                        borderStyle: 'dashed',
                        backgroundColor: isDragActive ? '#e3f2fd' : '#f8f9fa',
                        cursor: uploading ? 'not-allowed' : 'pointer'
                    }}
                >
                    <input {...getInputProps()} />
                    {isDragActive ? (
                        <div>
                            <i className="bi bi-cloud-arrow-up display-4 text-primary"></i>
                            <p className="mt-2">Suelta los archivos aquí...</p>
                        </div>
                    ) : (
                        <div>
                            <i className="bi bi-cloud-arrow-up display-4 text-muted"></i>
                            <p className="mt-2">
                                Arrastra imágenes aquí o haz clic para seleccionar
                            </p>
                            <small className="text-muted">
                                Formatos: JPG, PNG, GIF, WEBP • Máx. 10MB • Incluir código {product.productCode}
                            </small>
                        </div>
                    )}
                </div>

                {/* Input tradicional como alternativa */}
                <div className="mb-3">
                    <label className="form-label">O selecciona archivos manualmente:</label>
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="form-control"
                        multiple
                        accept="image/jpeg, image/png, image/gif, image/webp"
                        onChange={handleFileChange}
                        disabled={uploading}
                    />
                </div>

                {/* Resumen de selección - SOLO MOSTRAR SI HAY ALGO */}
                {(totalSelected > 0 || totalOmitted > 0) && (
                    <div className="mb-3 p-3 bg-light rounded">
                        <h6 className="mb-2">Resumen de selección:</h6>

                        {totalSelected > 0 && (
                            <p className="text-success mb-1">
                                <i className="bi bi-check-circle-fill me-2"></i>
                                <strong>{totalSelected}</strong> archivo(s) válido(s)
                            </p>
                        )}

                        {totalOmitted > 0 && (
                            <p className="text-warning mb-1">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                <strong>{totalOmitted}</strong> archivo(s) omitido(s)
                            </p>
                        )}
                    </div>
                )}

                {/* Archivos seleccionados */}
                {selectedFiles.length > 0 && (
                    <div className="mb-3">
                        <h6 className="text-success">
                            <i className="bi bi-check-circle-fill me-2"></i>
                            Archivos a subir ({selectedFiles.length})
                        </h6>
                        <div className="mt-2">
                            {selectedFiles.map((fileWithUsage, index) => (
                                <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-success bg-opacity-10 rounded">
                                    <div>
                                        <i className="bi bi-file-image me-2 text-success"></i>
                                        <span className="small">{fileWithUsage.file.name}</span>
                                        <span className="text-muted ms-2 small">
                                            ({Math.round(fileWithUsage.file.size / 1024)} KB)
                                            Uso: {USO_IMAGEN_LABELS[fileWithUsage.usoImagen]}
                                        </span>
                                    </div>
                                    <select
                                        className="form-select form-select-sm w-auto me-2"
                                        value={fileWithUsage.usoImagen}
                                        onChange={(e) => handleCambioUsoImagen(index, e.target.value as UsoImagenType)}
                                    >
                                        {Object.entries(USO_IMAGEN_LABELS).map(([key, label]) => (
                                            <option key={key} value={key}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => removeSingleFile(index)}
                                        disabled={uploading}
                                    >
                                        <i className="bi bi-x"></i>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Archivos omitidos por duplicados */}
                {duplicateFiles.length > 0 && (
                    <div className="mb-3">
                        <h6 className="text-warning">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            Duplicados omitidos ({duplicateFiles.length})
                        </h6>
                        <ul className="small text-muted">
                            {duplicateFiles.slice(0, 3).map((name, index) => (
                                <li key={index}>{name}</li>
                            ))}
                            {duplicateFiles.length > 3 && <li>...y {duplicateFiles.length - 3} más</li>}
                        </ul>
                    </div>
                )}

                {/* Archivos omitidos por código inválido */}
                {invalidCodeFiles.length > 0 && (
                    <div className="mb-3">
                        <h6 className="text-danger">
                            <i className="bi bi-x-circle-fill me-2"></i>
                            Código inválido ({invalidCodeFiles.length})
                        </h6>
                        <ul className="small text-muted">
                            {invalidCodeFiles.slice(0, 3).map((name, index) => (
                                <li key={index}>
                                    {name}
                                    <span className="text-danger ms-2">
                                        (falta {product.productCode})
                                    </span>
                                </li>
                            ))}
                            {invalidCodeFiles.length > 3 && <li>...y {invalidCodeFiles.length - 3} más</li>}
                        </ul>
                    </div>
                )}

                {/* Botones de acción - SOLO MOSTRAR SI HAY ARCHIVOS SELECCIONADOS */}
                {selectedFiles.length > 0 && (
                    <div className="d-flex gap-2 mt-4 pt-3 border-top">
                        <button
                            className="btn btn-primary"
                            onClick={localHandleUpload}
                            disabled={uploading}
                        >
                            {uploading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Subiendo...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-cloud-upload me-2" />
                                    Subir {selectedFiles.length} archivo(s)
                                </>
                            )}
                        </button>

                        <button
                            className="btn btn-outline-secondary"
                            onClick={handleClearSelection}
                            disabled={uploading}
                        >
                            <i className="bi bi-x-circle me-2" />
                            Cancelar y limpiar
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageUploader;